/*!
 * Copyright © 2025. Verizon Connect Ireland Limited. All rights reserved.
 */

import { AfterViewInit, ChangeDetectionStrategy, Component } from '@angular/core';
import { IUserPreferencesModel } from '@fleetmatics/ui.insights-panel';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { WindowResizeService } from '../../services';
import { CloseInsightsPanel } from '../../store/actions';
import { getIsFullscreen, getUserSettings } from '../../store/selectors';
import { IAppState } from '../../store/state';

@Component({
  selector: 'fm-vzc-side-drawer-container',
  templateUrl: './vzc-side-drawer-container.component.html',
  styleUrls: ['vzc-side-drawer-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VzcSideDrawerContainerComponent implements AfterViewInit {
  private readonly _unsubscribe$ = new Subject<void>();
  drawerMode = 'overlay';
  userPreferences$: Observable<IUserPreferencesModel | undefined> = this._store.pipe(
    select(getUserSettings),
    map(settings => {
      if (!settings?.TimeZoneAndroidId || !settings?.Language) {
        return undefined;
      }

      return {
        timezone: settings.TimeZoneAndroidId,
        language: settings.Language
      };
    })
  );

  isFullscreen$ = this._store.pipe(select(getIsFullscreen));
  isMobileOrTablet$ = this._windowResizeService.isMobileOrTablet$;

  constructor(
    private readonly _store: Store<IAppState>,
    private readonly _windowResizeService: WindowResizeService
  ) {}

  ngAfterViewInit(): void {
    combineLatest([this.userPreferences$, this.isMobileOrTablet$])
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(([userPreferences, isMobileOrTablet]) => {
        if (!userPreferences) {
          return;
        }

        if (isMobileOrTablet) {
          this._store.dispatch(new CloseInsightsPanel());
        }
      });
  }
}
